import React, { useState, useEffect, useMemo } from "react";
import {
  InfoContainer,
  Title,
  TableActionHeader,
} from "./ConcreteTracesModalStyle.jsx";

import { TableEdit } from "./TableEdit";

const formatPercent = (value) => {
  if (value === null) {
    return "-";
  }
  return `${(parseFloat(value) * 100).toFixed(1)}%`;
};

const formatNumber = (value) => {
  if (value === null) {
    return "-";
  }
  return parseFloat(value).toFixed(1);
};

const materialHeaders = (inventoryItems, inventoryLocations) => ({
  id: {
    label: "Ordem",
  },
  item: {
    label: "Material",
    isEditable: true,
    component: "select",
    options: inventoryItems.map(({
      code, name, materialType, unit
    }) => ({
      value: code, label: name, materialType, unit
    }))
  },
  location: {
    label: "Local de Estoque",
    isEditable: true,
    component: "select",
    options: inventoryLocations.map(({
      code, name, materialType, unit
    }) => ({
      value: code, label: name, materialType, unit
    })),
  },
  humidity: {
    label: "Umidade",
    isEditable: true,
    component: "text",
    formatter: (value) => formatPercent(value),
    isValid: value => !isNaN(value) && value >= 0
  },
  weight: {
    label: "Traço (kg)",
    isEditable: true,
    component: "text",
    formatter: (value) => formatNumber(value),
    isValid: value => !isNaN(value) && value >= 0
  },
  materialWeight: {
    label: "Carga (kg)",
    isEditable: false,
    component: "text",
    formatter: (value) => formatNumber(value),
    // isValid: value => !isNaN(value) && value >= 0
  },
  supplier: {
    label: "Fornecedor",
    isEditable: true,
    component: "text",
  },
});

export const FormulaMaterialTable = ({
  // locationItemLink,
  setParams,
  inventoryItems,
  inventoryLocations,
  params: {
    quantity,
    materials,
  },
  water,
  sands,
  waterCut,
  setWaterQuantity,
}) => {
  const weightByMaterial = useMemo(() => {
    return (materials || []).reduce((acc, { weight, locations, order }) => {
      const formula = parseFloat(
        locations.length
          ? locations.reduce((sum, { weight: locWeight }) => sum + parseFloat(locWeight), 0)
          : weight
      );
      acc[order] = {
        formula,
        shipment: formula * quantity,
      };
      return acc;
    }, {});
  }, [materials, quantity]);

  const [correctedMaterialQuantity, setCorrectedMaterialQuantity] = useState({});
  const ignoreItems = new Set(["SAND", "WATER"]);

  const mapDataToTable = (data, items = [], locationsData = []) => {
    const tableData = [];
    data.forEach(({
      materialCode,
      supplier,
      order,
      locations,
      humidity,
    }) => {
      const item = items.find((elem) => elem.code === materialCode);
      if (locations.length > 0) {
        locations.forEach(({
          locationCode,
        }) => {
          const location = locationsData.find((elem) => elem.code === locationCode);
          tableData.push({
            id: order,
            item: item ? { value: item.code, label: item.name } : { value: null, label: null },
            location: location ? { value: location.code, label: location.name } : { value: null, label: null },
            humidity,
            weight: weightByMaterial[order].formula,
            materialWeight: correctedMaterialQuantity[order],
            supplier,
          });
        });
      } else {
        tableData.push({
          id: order,
          item: item ? { value: item.code, label: item.name } : { value: null, label: null },
          location: { value: null, label: null },
          humidity,
          weight: weightByMaterial[order].formula,
          materialWeight: correctedMaterialQuantity[order],
          supplier,
        });
      }
    });
    return tableData;
  };

  const [tableData, setTableData] = useState([]);
  const [initialValues, setInitialValues] = useState([]);

  const headers = useMemo(
    () => materialHeaders(inventoryItems || [], inventoryLocations || []),
    [inventoryItems, inventoryLocations]
  );

  const correctQuantity = (shipmentMaterials, items) => {
    const codes = new Set(
      items
        .filter(({ materialType }) => !ignoreItems.has(materialType))
        .reduce((acc, { code }) => {
          acc.push(code);
          return acc;
        }, [])
    );
    shipmentMaterials.forEach(({ materialCode, order }) => {
      if (codes.has(materialCode)) {
        setCorrectedMaterialQuantity((prev) => ({
          ...prev,
          [order]: weightByMaterial[order].shipment
        }));
      }
    });
  };

  const calculateSandsQuantity = (shipmentMaterials) => {
    let discount = 0;
    sands.forEach(elem => {
      shipmentMaterials.forEach(({ materialCode, humidity, order }) => {
        if (materialCode === elem.code) {
          const correctedMaterial = (1 + parseFloat(humidity)) * weightByMaterial[order].shipment;
          discount += weightByMaterial[order].shipment * humidity;
          setCorrectedMaterialQuantity((prev) => ({
            ...prev,
            [order]: correctedMaterial,
          }));
        }
      });
    });
    return discount;
  };

  const calculateWaterQuantity = (shipmentMaterials, discount, cut) => {
    shipmentMaterials.forEach(({ materialCode, order }) => {
      if (materialCode === water.code) {
        const newQuantity = weightByMaterial[order].shipment - discount;
        setWaterQuantity(newQuantity);
        setCorrectedMaterialQuantity((prev) => ({
          ...prev,
          [order]: newQuantity - cut,
        }));
      }
    });
  };

  useEffect(() => {
    if (inventoryItems && inventoryItems.length > 0) {
      correctQuantity(materials, inventoryItems);
    }
  }, [inventoryItems, materials, weightByMaterial]);

  useEffect(() => {
    if (water && sands && materials) {
      const discount = calculateSandsQuantity(materials);
      calculateWaterQuantity(materials, discount, waterCut);
    }
  }, [water, sands, materials, waterCut, quantity]);

  useEffect(() => {
    if (materials && inventoryItems && inventoryLocations) {
      const newTableData = mapDataToTable(materials, inventoryItems, inventoryLocations);
      setTableData(newTableData);
      if (initialValues && initialValues.length === 0) {
        setInitialValues(newTableData);
      }
    };
  }, [materials, inventoryItems, inventoryLocations, weightByMaterial, correctedMaterialQuantity]);

  const handleChange = ({ id, key, newValue }) => {
    const newTableData = tableData.map(row => {
      if (row.id === id) {
        return {
          ...row,
          [key]: newValue,
        };
      }
      return row;
    });

    setTableData(newTableData);

    const updatedMaterialsMap = new Map();

    newTableData.forEach(row => {
      const code = row.item.value;
      const locationCode = row.location.value;

      if (!updatedMaterialsMap.has(row.id)) {
        updatedMaterialsMap.set(row.id, {
          order: row.id,
          materialCode: code,
          humidity: row.humidity,
          supplier: row.supplier,
          weight: row.weight,
          locations: [],
        });
      }

      const mat = updatedMaterialsMap.get(row.id);
      if (locationCode) {
        mat.locations.push({
          locationCode,
          weight: row.weight,
          materialCode: null,
        });
      }
    });

    const updatedMaterials = Array.from(updatedMaterialsMap.values());

    setParams(prev => ({
      ...prev,
      materials: updatedMaterials,
    }));
  };

  return (
    <React.Fragment>
      <InfoContainer>
        <TableActionHeader>
          <Title>Materiais</Title>
        </TableActionHeader>
      </InfoContainer>
      <InfoContainer>
        {tableData && headers && (
          <TableEdit
            tableName="materials"
            headers={headers}
            items={tableData}
            initialValues={initialValues}
            onChange={handleChange}
          />
        )}
      </InfoContainer>
    </React.Fragment>
  );
};
